import { omit } from "lodash-es";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import type { Board } from "@/model/board";
import type { Card } from "@/model/card";
import type { QueryImpl, SearchQuery } from "@/router/types";
import { asReadonly } from "@/utils/vue";

type StickyRef = Pick<Card, "id" | "groupId">;

export const useUrlStore = defineStore("url", () => {
  const search = ref<SearchQuery>({});

  const route = useRoute();
  const router = useRouter();

  // Sticky targets parsed from the URL and consumed by the sticky components once they
  // mount (see useEnlarge/useSelect). Pulling on mount — instead of firing an event once
  // on boardLoaded — avoids dropping the action when the target sticky has not rendered
  // yet (slow backend or a late-resolved objective/dependency sticky).
  const enlargeStickyId = ref<string | null>(null);
  const selectStickyIds = ref<string[]>([]);
  const focusStickyId = ref<string | null>(null);

  let fromQuery: QueryImpl = {};
  watch(
    route,
    (route) => {
      const toQuery = route.query as QueryImpl;
      if (
        toQuery.searchText !== fromQuery.searchText ||
        toQuery.searchFlag !== fromQuery.searchFlag ||
        toQuery.searchAssignee !== fromQuery.searchAssignee ||
        toQuery.searchReporter !== fromQuery.searchReporter ||
        toQuery.searchTeam !== fromQuery.searchTeam ||
        toQuery.searchArt !== fromQuery.searchArt ||
        toQuery.searchIteration !== fromQuery.searchIteration ||
        toQuery.searchType !== fromQuery.searchType ||
        toQuery.searchDepLink !== fromQuery.searchDepLink ||
        toQuery.searchStatus !== fromQuery.searchStatus ||
        toQuery.searchLink !== fromQuery.searchLink ||
        toQuery.searchSignal !== fromQuery.searchSignal ||
        toQuery.searchUpperLevelLinks !== fromQuery.searchUpperLevelLinks
      ) {
        search.value = toSearchQuery(toQuery);
      }
      // React to any change (including removal) so an unrelated query update does not
      // re-trigger an enlarge/focus a sticky already consumed, and a removed param clears
      // the target rather than leaving a stale one for a late-rendering sticky to act on.
      if (toQuery.stickyId !== fromQuery.stickyId) {
        const ids = toQuery.stickyId?.split(",") ?? [];
        enlargeStickyId.value = ids.length === 1 ? ids[0] : null;
        selectStickyIds.value = ids.length > 1 ? ids : [];
      }
      if (toQuery.highlightStickyId !== fromQuery.highlightStickyId) {
        focusStickyId.value = toQuery.highlightStickyId ?? null;
      }
      fromQuery = { ...toQuery };
    },
    { immediate: true },
  );

  function consumeEnlargeSticky(card: StickyRef, board: Board) {
    if (!isTarget(enlargeStickyId.value, card, board)) return false;
    enlargeStickyId.value = null;
    return true;
  }

  function consumeSelectedSticky(card: StickyRef, board: Board) {
    const target = selectStickyIds.value.find((targetId) => isTarget(targetId, card, board));
    if (!target) return false;
    selectStickyIds.value = selectStickyIds.value.filter((targetId) => targetId !== target);
    return true;
  }

  function consumeFocusedSticky(card: StickyRef, board: Board) {
    if (!isTarget(focusStickyId.value, card, board)) return false;
    focusStickyId.value = null;
    // so that a later link to the same sticky changes the URL again
    removeQueryParam("highlightStickyId");
    return true;
  }

  function removeEnlargeStickyFromUrl(card: StickyRef, board: Board) {
    if (isTarget(fromQuery.stickyId ?? null, card, board)) {
      removeQueryParam("stickyId");
    }
  }

  function removeQueryParam(key: keyof QueryImpl) {
    void router.replace({ query: omit(route.query, key) });
  }

  function isTarget(target: string | null, card: StickyRef, board: Board) {
    return !!target && (target === card.id || target === card.groupId) && isRouteBoard(board);
  }

  // Mirrored sticky on the board being left must not consume a target meant for the next board
  function isRouteBoard(board: Board) {
    return (
      route.params.board === board.type &&
      (board.type !== "team" || route.params.team === board.team.name)
    );
  }

  return {
    search: asReadonly(search),
    enlargeStickyId: asReadonly(enlargeStickyId),
    selectStickyIds: asReadonly(selectStickyIds),
    focusStickyId: asReadonly(focusStickyId),

    consumeEnlargeSticky,
    consumeSelectedSticky,
    consumeFocusedSticky,
    removeEnlargeStickyFromUrl,
  };
});

function toSearchQuery(q: QueryImpl): SearchQuery {
  return {
    text: q.searchText,
    flag: q.searchFlag,
    assignee: q.searchAssignee,
    reporter: q.searchReporter,
    team: q.searchTeam,
    art: q.searchArt,
    iteration: q.searchIteration,
    type: q.searchType,
    depLink: q.searchDepLink,
    link: q.searchLink,
    signal: q.searchSignal,
    status: q.searchStatus,
    upperLevelLinks: q.searchUpperLevelLinks,
  };
}
